import { useCallback, useEffect, useRef, useState } from 'react';

import { api, ApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth-store';

// ============================================
// Types
// ============================================

export type SsoTargetSystem = 'flow' | 'twenty' | 'chatwoot';
export type IframeSsoStatus =
  | 'idle'
  | 'requesting'
  | 'sending'
  | 'success'
  | 'fallback'
  | 'unavailable';

export interface UseIframeSsoOptions {
  targetOrigin: string;
  targetSystem: SsoTargetSystem;
  onSuccess?: () => void;
  onFallback?: () => void;
  enabled?: boolean;
}

export interface UseIframeSsoResult {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  status: IframeSsoStatus;
  retry: () => void;
  sendLogout: () => void;
}

/** Shape of the postMessage sent to the iframe. */
interface SsoInitMessage {
  type: 'rpi:sso:init';
  ssoToken: string;
}

/** Shape of the acknowledgment messages received from the iframe. */
interface SsoAckMessage {
  type: 'rpi:sso:ack' | 'rpi:sso:fail';
}

/** Backend response from POST /api/v1/auth/sso/token */
interface SsoTokenResponse {
  data: {
    nonce: string;
    expiresIn: number;
  };
}

// ============================================
// Constants
// ============================================

/** Time to wait for iframe SSO ack/fail before falling back (ms). */
const SSO_TIMEOUT_MS = 10_000;

/** Delay before resending postMessage if no ack received (ms). */
const SSO_RETRY_DELAY_MS = 2_000;

/** Maximum number of postMessage retries before waiting for the overall timeout. */
const SSO_MAX_RETRIES = 3;

// ============================================
// Hook
// ============================================

/**
 * useIframeSso — postMessage-based SSO bridge for embedded iframes.
 *
 * Flow:
 * 1. iframe onLoad fires -> hook requests SSO token from backend
 * 2. Backend returns { ssoToken, expiresAt }
 * 3. Hook sends postMessage to iframe with the token
 * 4. Hook listens for ack/fail from iframe
 * 5. 10s timeout -> fallback if no response
 *
 * Usage:
 * ```tsx
 * const { iframeRef, status } = useIframeSso({
 *   targetOrigin: 'https://flow.mflerp.com',
 *   targetSystem: 'flow',
 * });
 * return <iframe ref={iframeRef} src="..." />;
 * ```
 */
export function useIframeSso(options: UseIframeSsoOptions): UseIframeSsoResult {
  const { targetOrigin, targetSystem, onSuccess, onFallback, enabled = true } = options;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [status, _setStatus] = useState<IframeSsoStatus>('idle');
  // Keep a ref in sync with status so performSsoHandshake can read it synchronously
  const statusRef = useRef<IframeSsoStatus>('idle');
  const setStatus = useCallback((next: IframeSsoStatus) => {
    statusRef.current = next;
    _setStatus(next);
  }, []);

  // Use refs for callbacks so we don't re-trigger effects when they change
  const onSuccessRef = useRef(onSuccess);
  const onFallbackRef = useRef(onFallback);
  onSuccessRef.current = onSuccess;
  onFallbackRef.current = onFallback;

  // Track active timeout so we can clean it up
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track retry interval for postMessage resends
  const retryIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Track whether the hook is still mounted
  const mountedRef = useRef(true);
  // Track the load handler so we can remove it on cleanup
  const loadHandlerRef = useRef<(() => void) | null>(null);

  // Cleanup helper — clears both the overall timeout and the retry interval
  const clearSsoTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (retryIntervalRef.current !== null) {
      clearInterval(retryIntervalRef.current);
      retryIntervalRef.current = null;
    }
  }, []);

  /**
   * Core SSO handshake logic — extracted so `retry()` can call it too.
   *
   * Finding 13 fix: if a handshake is already in progress (requesting/sending/success),
   * skip to prevent duplicate handshakes from multiple iframe load events.
   *
   * Finding 14 fix: after sending the postMessage, retry up to SSO_MAX_RETRIES times
   * at SSO_RETRY_DELAY_MS intervals in case the bridge JS hasn't initialized yet.
   */
  const performSsoHandshake = useCallback(async () => {
    if (!mountedRef.current) return;

    // Guard: don't start a new handshake if one is already in progress or succeeded
    const currentStatus = statusRef.current;
    if (
      currentStatus === 'requesting' ||
      currentStatus === 'sending' ||
      currentStatus === 'success'
    ) {
      return;
    }

    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) {
      setStatus('fallback');
      onFallbackRef.current?.();
      return;
    }

    // Step 1: Request SSO token from backend
    setStatus('requesting');

    let ssoToken: string;
    try {
      const response = await api.post<SsoTokenResponse>('/auth/sso/token', {
        targetSystem,
      });
      ssoToken = response.data.nonce;
    } catch (error: unknown) {
      if (!mountedRef.current) return;
      // 503 = SSO service unavailable (Redis not configured, target not deployed)
      if (error instanceof ApiError && error.statusCode === 503) {
        setStatus('unavailable');
        return;
      }
      setStatus('fallback');
      onFallbackRef.current?.();
      return;
    }

    if (!mountedRef.current) return;

    // Step 2: Send token to iframe via postMessage
    setStatus('sending');

    const message: SsoInitMessage = {
      type: 'rpi:sso:init',
      ssoToken,
    };

    iframe.contentWindow.postMessage(message, targetOrigin);

    // Step 3: Start overall timeout — if no ack/fail within 10s, fall back
    clearSsoTimeout();
    timeoutRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      clearSsoTimeout(); // Also clears retry interval
      setStatus('fallback');
      onFallbackRef.current?.();
    }, SSO_TIMEOUT_MS);

    // Step 4: Retry postMessage at intervals — bridge JS may not be ready on first send
    let retryCount = 0;
    retryIntervalRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      // Stop retrying if ack was received (status changed from 'sending')
      if (statusRef.current !== 'sending') {
        if (retryIntervalRef.current !== null) {
          clearInterval(retryIntervalRef.current);
          retryIntervalRef.current = null;
        }
        return;
      }

      retryCount++;
      if (retryCount >= SSO_MAX_RETRIES) {
        if (retryIntervalRef.current !== null) {
          clearInterval(retryIntervalRef.current);
          retryIntervalRef.current = null;
        }
        return; // Let the overall timeout handle the fallback
      }

      // Resend — iframe bridge may have initialized since last attempt
      const currentIframe = iframeRef.current;
      if (currentIframe?.contentWindow) {
        currentIframe.contentWindow.postMessage(message, targetOrigin);
      }
    }, SSO_RETRY_DELAY_MS);
  }, [targetSystem, targetOrigin, clearSsoTimeout, setStatus]);

  // ---- Message listener for ack/fail ----
  useEffect(() => {
    if (!enabled) return;

    const handleMessage = (event: MessageEvent<unknown>) => {
      // Security: only accept messages from the expected origin
      if (event.origin !== targetOrigin) return;

      // Validate message shape
      const data = event.data;
      if (!isSsoAckMessage(data)) return;

      clearSsoTimeout();

      if (data.type === 'rpi:sso:ack') {
        setStatus('success');
        onSuccessRef.current?.();
      } else {
        if (statusRef.current === 'success') return; // Ignore stale fail from reloaded iframe
        setStatus('fallback');
        onFallbackRef.current?.();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [enabled, targetOrigin, clearSsoTimeout, setStatus]);

  // ---- iframe onLoad handler via iframeRef observation ----
  //
  // We use a separate effect that watches `enabled` and `performSsoHandshake`,
  // plus we expose an `onIframeLoad` event attachment. Because `iframeRef` is a
  // plain ref, React does not re-run effects when it changes. Instead, the
  // consumer attaches the ref to the <iframe> and the iframe's native `load`
  // event triggers the handshake. We attach this listener in the effect and
  // also re-attach if the ref changes (checked via interval or MutationObserver
  // would be overkill). The practical pattern: the effect sets up a listener on
  // `iframeRef.current` if available, and the `retry()` function can be used to
  // kick off the handshake manually if the ref was set after mount.
  useEffect(() => {
    if (!enabled) return;

    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      void performSsoHandshake();
    };

    loadHandlerRef.current = handleLoad;
    iframe.addEventListener('load', handleLoad);
    return () => {
      iframe.removeEventListener('load', handleLoad);
      loadHandlerRef.current = null;
    };
  }, [enabled, performSsoHandshake]);

  // ---- Cleanup on unmount ----
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearSsoTimeout();
    };
  }, [clearSsoTimeout]);

  // ---- Retry function ----
  const retry = useCallback(() => {
    clearSsoTimeout();
    setStatus('idle');
    void performSsoHandshake();
  }, [clearSsoTimeout, performSsoHandshake, setStatus]);

  // ---- Send logout to iframe ----
  const sendLogout = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage({ type: 'rpi:sso:logout' }, targetOrigin);
  }, [targetOrigin]);

  // ---- Auto-propagate logout when parent auth state changes ----
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const prevAuthRef = useRef(isAuthenticated);

  useEffect(() => {
    if (!enabled) return;

    // Detect transition from authenticated → unauthenticated
    if (prevAuthRef.current && !isAuthenticated) {
      sendLogout();
    }
    prevAuthRef.current = isAuthenticated;
  }, [enabled, isAuthenticated, sendLogout]);

  return { iframeRef, status, retry, sendLogout };
}

// ============================================
// Type guard
// ============================================

function isSsoAckMessage(data: unknown): data is SsoAckMessage {
  if (typeof data !== 'object' || data === null) return false;
  const msg = data as Record<string, unknown>;
  return msg['type'] === 'rpi:sso:ack' || msg['type'] === 'rpi:sso:fail';
}
